import { useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import { errorCodeSelector, userSelector, companiesSelector } from './store/selector';
import { meApiService } from './services/api';
import {
  getMeCompaniesFailureApiAction,
  getMeCompaniesInitApiAction,
  getMeCompaniesSuccessApiAction,
  getMeUserFailureApiAction,
  getMeUserInitApiAction,
  getMeUserSuccessApiAction,
  meClearAction,
  putMePasswordFailureApiAction,
  putMePasswordInitApiAction,
  putMePasswordSuccessApiAction,
  putMeUserFailureApiAction,
  putMeUserInitApiAction,
  putMeUserSuccessApiAction,
} from './store/reducer';
import { PutMeUserPayloadRequest } from './interfaces/put-me-user';
import { PutMePasswordPayloadRequest } from './interfaces/put-me-password';

export function useMeCore() {
  const user = useSelector(userSelector);
  const companies = useSelector(companiesSelector);
  const errorCode = useSelector(errorCodeSelector);
  const dispatch = useDispatch();

  const getMeUser = useCallback(
    async (onSuccess?: () => void) => {
      try {
        dispatch(getMeUserInitApiAction());
        const response = await meApiService.getMeUser();
        dispatch(getMeUserSuccessApiAction(response));
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        dispatch(
          getMeUserFailureApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const putMeUser = useCallback(
    async (
      body: PutMeUserPayloadRequest,
      onSuccess?: () => void,
      onFailure?: (error: any) => void
    ) => {
      try {
        dispatch(putMeUserInitApiAction());
        await meApiService.putMeUser(body);
        dispatch(putMeUserSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.(error);

        dispatch(
          putMeUserFailureApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const putMePassword = useCallback(
    async (
      body: PutMePasswordPayloadRequest,
      onSuccess?: () => void,
      onFailure?: (error: any) => void // <-- Ya lo tenías, ¡genial!
    ) => {
      try {
        dispatch(putMePasswordInitApiAction());
        await meApiService.putMePassword(body);
        dispatch(putMePasswordSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error("Error al cambiar contraseña:", error);
        onFailure?.(error);

        dispatch(
          putMePasswordFailureApiAction({ errorCode: error.response?.status || 500 })
        );
      }
    },
    [dispatch]
  );

  const getMeCompanies = useCallback(
    async (onSuccess?: () => void) => {
      try {
        dispatch(getMeCompaniesInitApiAction());
        const response = await meApiService.getMeCompanies();
        dispatch(getMeCompaniesSuccessApiAction(response));
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        dispatch(
          getMeCompaniesFailureApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const clearMe = useCallback(() => {
    dispatch(meClearAction());
  }, [dispatch]);

  return {
    user,
    companies,
    errorCode,
    getMeUser,
    putMeUser,
    getMeCompanies,
    putMePassword,
    clearMe,
  };
}
